
// Deployment trigger: 2026-04-24
import React, { useState, useCallback } from 'react';
import { GameType, Prize, Question } from '../types';
import { LeadForm, SuccessView, TicketView, GameWrapper } from './games/Common';
import SpinWheel from './games/SpinWheel';
import ScratchCard from './games/ScratchCard';
import SlotMachine from './games/SlotMachine';
import MysteryBox from './games/MysteryBox';
import Raffle from './games/Raffle';
import Trivia from './games/Trivia';
import GiftRain from './games/GiftRain';
import { db } from '../firebase';
import { collection, addDoc, serverTimestamp, doc, updateDoc, increment, getDoc, query, where, getDocs, writeBatch } from 'firebase/firestore';
import { sendBrevoEmail } from '../services/emailService';
import { sendSignalWireSms } from '../services/smsService';
import { drawPrize } from '../services/drawService';

interface GameSimulatorProps {
  type: GameType;
  prizes: Prize[];
  questions?: Question[];
  campaignName?: string;
  endDate?: string;
  campaignId?: string;
  projectId?: string;
  ownerId?: string;
  businessName?: string;
  theme?: string;
  buttonText?: string;
  isLightMode?: boolean;
}

const GameSimulator: React.FC<GameSimulatorProps> = ({ 
  type, 
  prizes, 
  questions = [], 
  endDate, 
  campaignName = "Game Campaign",
  campaignId = "local-test",
  projectId = "local-project",
  ownerId,
  businessName = "Business Name",
  theme = "casino",
  buttonText = "SPIN",
  isLightMode = false
}) => {
  const [gameState, setGameState] = useState<'idle' | 'playing' | 'revealed' | 'won' | 'lead_form' | 'ticket_ready' | 'submitting' | 'already_entered'>('idle');
  const [rotation, setRotation] = useState(0);
  const [winningPrize, setWinningPrize] = useState<Prize | null>(null);
  // Identifier of the server-issued draw that authoritatively chose the prize.
  const [drawId, setDrawId] = useState<string>('');
  const [activeSelection, setActiveSelection] = useState<number | null>(null);
  const [slotValues, setSlotValues] = useState<number[]>([0, 0, 0]);
  const [ticketNumber, setTicketNumber] = useState('');
  
  // Lead Form state
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [globalConfig, setGlobalConfig] = useState<any>(null);

  const effectiveLightMode = isLightMode ?? true;

  React.useEffect(() => {
    console.log(`[GameSimulator] Initialized with campaignId: ${campaignId}, projectId: ${projectId}, type: ${type}`);
    
    if (campaignId !== 'local-test') {
      const hasEntered = localStorage.getItem(`entered_${campaignId}_${type}`);
      if (hasEntered) {
        setGameState('already_entered');
      }
    }

    const fetchConfig = async () => {
      try {
        let currentOwnerId = ownerId;
        if (!currentOwnerId && projectId && projectId !== 'local-project') {
          const projectRef = doc(db, 'projects', projectId);
          const projectSnap = await getDoc(projectRef);
          if (projectSnap.exists()) {
            currentOwnerId = projectSnap.data().ownerId;
          }
        }
        if (currentOwnerId) {
          const userRef = doc(db, 'users', currentOwnerId);
          const userSnap = await getDoc(userRef);
          if (userSnap.exists()) {
            setGlobalConfig(userSnap.data().globalConfig);
          }
        }
      } catch (e) {
        console.warn("Failed to fetch global config", e);
      }
    };
    fetchConfig();
  }, [campaignId, projectId, ownerId]);

  const pickPrize = useCallback(() => {
    const totalWeight = prizes.reduce((sum, p) => sum + p.probability, 0);
    let random = Math.random() * totalWeight;
    for (let i = 0; i < prizes.length; i++) {
      if (random < prizes[i].probability) return prizes[i];
      random -= prizes[i].probability;
    }
    return prizes[0];
  }, [prizes]);

  // Resolve the winning prize. For real campaigns the server is the source of
  // truth (outcome can't be rigged on the client); local previews fall back to
  // a client-side pick. Returns null if the draw failed (caller should abort).
  const resolvePrize = async (lossOutcome?: 'loss'): Promise<Prize | null> => {
    if (campaignId === 'local-test') {
      if (lossOutcome === 'loss') return prizes.find(p => p.type === 'empty') || prizes[prizes.length - 1];
      return pickPrize();
    }
    const result = await drawPrize(campaignId, lossOutcome);
    setDrawId(result.drawId);
    const prize = prizes.find(p => p.id === result.prizeId);
    if (!prize) throw new Error('Could not start the game. Please try again.');
    return prize;
  };

  const handleStartGame = async (index?: number) => {
    if (gameState !== 'idle') return;

    if (type === GameType.RAFFLE) {
      setGameState('lead_form');
      return;
    }

    // Block re-entry and resolve the (server-authoritative) prize first.
    setGameState('submitting');
    let resolved: Prize | null;
    try {
      resolved = await resolvePrize();
    } catch (e: any) {
      console.error('Draw failed:', e);
      alert(e.message || 'Could not start the game. Please try again.');
      setGameState('idle');
      return;
    }
    if (!resolved) { setGameState('idle'); return; }
    const prize = resolved;

    setWinningPrize(prize);
    setGameState('playing');
    if (index !== undefined) setActiveSelection(index);

    // Engine specific starts
    if (type === GameType.SPIN_WHEEL) {
      const prizeIdx = prizes.findIndex(p => p.id === prize.id);
      const segmentAngle = 360 / prizes.length;
      const extraSpins = 8 + Math.floor(Math.random() * 4); 
      const targetAngle = (extraSpins * 360) + (360 - (prizeIdx * segmentAngle) - (segmentAngle / 2));
      setRotation(targetAngle);
      setTimeout(() => { setGameState('revealed'); setTimeout(() => setGameState('won'), 2000); }, 4000);
    } else if (type === GameType.SLOT_MACHINE) {
      const interval = setInterval(() => {
        setSlotValues([Math.floor(Math.random() * prizes.length), Math.floor(Math.random() * prizes.length), Math.floor(Math.random() * prizes.length)]);
      }, 100);
      setTimeout(() => {
        clearInterval(interval);
        const winIdx = prizes.findIndex(p => p.id === prize.id);
        setSlotValues([winIdx, winIdx, winIdx]);
        setGameState('revealed'); setTimeout(() => setGameState('won'), 1500);
      }, 3000);
    } else if (type === GameType.MYSTERY_BOX) {
      setTimeout(() => { setGameState('revealed'); setTimeout(() => setGameState('won'), 1500); }, 2000);
    }
  };

  const handleScratchProgress = (progress: number) => {
    if (progress > 65 && gameState === 'playing') {
      setGameState('revealed');
      setTimeout(() => setGameState('won'), 2500);
    }
  };

  const handleTriviaWin = async () => {
    setGameState('submitting');
    try {
      const prize = await resolvePrize();
      if (!prize) { setGameState('idle'); return; }
      setWinningPrize(prize);
      setGameState('won');
    } catch (e: any) {
      alert(e.message || 'Could not complete the game. Please try again.');
      setGameState('idle');
    }
  };

  const handleTriviaLoss = async () => {
    setGameState('submitting');
    try {
      const prize = await resolvePrize('loss');
      if (!prize) { setGameState('idle'); return; }
      setWinningPrize(prize);
      setGameState('won');
    } catch (e: any) {
      alert(e.message || 'Could not complete the game. Please try again.');
      setGameState('idle');
    }
  };

  const handleSubmitLead = async () => {
    setGameState('submitting');
    console.log(`[GameSimulator] Submitting lead. ProjectId from props: ${projectId}, CampaignId: ${campaignId}`);
    const isRaffle = type === GameType.RAFFLE;
    const generatedTicket = isRaffle ? `TKT-${Math.floor(100000 + Math.random() * 900000)}` : '';
    
    try {
      const campaignCode = campaignId !== 'local-test' ? campaignId.substring(0, 6).toUpperCase() : 'TEST01';
      const finalProjectId = projectId || 'unknown';

      // 1. Check Project Quotas & Duplicates
      if (projectId && projectId !== 'local-project') {
         const projectDoc = await getDoc(doc(db, 'projects', projectId));
         if (projectDoc.exists()) {
            const pData = projectDoc.data();
            // Basic quota enforcement check
            if (email && pData.emailUsed >= pData.emailQuota) {
               throw new Error("Monthly email quota exceeded for this project.");
            }
            if (phone && pData.smsUsed >= pData.smsQuota) {
               throw new Error("Monthly SMS quota exceeded for this project.");
            }
         }

         // Duplicate checking via campaign_entries & Save Lead
         const batch = writeBatch(db);
         if (email) {
           const emailKey = email.toLowerCase().replace(/[^a-z0-9@.-]/g, '');
           batch.set(doc(db, 'campaign_entries', `${campaignId}_${type}_email_${emailKey}`), { createdAt: serverTimestamp() });
         }
         if (phone) {
           const phoneKey = phone.replace(/[^0-9]/g, '');
           batch.set(doc(db, 'campaign_entries', `${campaignId}_${type}_phone_${phoneKey}`), { createdAt: serverTimestamp() });
         }
         
         const newLeadRef = doc(collection(db, 'leads'));
         batch.set(newLeadRef, {
           campaignId,
           projectId: finalProjectId,
           name,
           email,
           phone,
           prizeWon: winningPrize?.name || (isRaffle ? 'Raffle Entry' : 'N/A'),
           drawId,
           ticketNumber: generatedTicket,
           campaignCode: campaignCode,
           createdAt: new Date().toISOString(),
           serverCreatedAt: serverTimestamp()
         });

         try {
           await batch.commit();
           console.log(`[Lead] Saved lead for project: ${finalProjectId}, campaign: ${campaignId}`);
         } catch (e: any) {
           console.warn("Duplicate entry detected:", e);
           throw new Error("You have already entered this game. Only one entry per person is allowed.");
         }
      } else {
         // Local test fallback
         await addDoc(collection(db, 'leads'), {
           campaignId,
           projectId: 'local-project',
           name,
           email,
           phone,
           prizeWon: winningPrize?.name || (isRaffle ? 'Raffle Entry' : 'N/A'),
           drawId,
           ticketNumber: generatedTicket,
           campaignCode: campaignCode,
           createdAt: new Date().toISOString(),
           serverCreatedAt: serverTimestamp()
         });
      }

      // Check if they are new to the project (for myeSalon sync)
      let isNewToProject = true;
      if (projectId && projectId !== 'local-project') {
         const projBatch = writeBatch(db);
         if (email) {
           const emailKey = email.toLowerCase().replace(/[^a-z0-9@.-]/g, '');
           projBatch.set(doc(db, 'project_entries', `${projectId}_email_${emailKey}`), { createdAt: serverTimestamp() });
         }
         if (phone) {
           const phoneKey = phone.replace(/[^0-9]/g, '');
           projBatch.set(doc(db, 'project_entries', `${projectId}_phone_${phoneKey}`), { createdAt: serverTimestamp() });
         }
         try {
           await projBatch.commit();
           isNewToProject = true;
         } catch (e) {
           console.log("User already exists in project, skipping myeSalon sync.");
           isNewToProject = false;
         }
      }

      // 3. Increment Counters in Firestore (Quota Tracking)
      let finalBusinessName = businessName;
      if (projectId && projectId !== 'local-project') {
         const projectRef = doc(db, 'projects', projectId);

         // Quota increment is a public, bounded (+1) write and must run
         // independently of the project read below (which is not public).
         try {
            const updateData: any = {};
            if (email) updateData.emailUsed = increment(1);
            if (phone) updateData.smsUsed = increment(1);
            if (Object.keys(updateData).length > 0) {
              await updateDoc(projectRef, updateData);
            }
         } catch (e) {
            console.warn("Could not update project quota", e);
            // Lead is already saved; continue with notifications regardless.
         }

         // Optional business-name fallback. Reading the project doc requires
         // auth, so for public players this is expected to fail — fall back to
         // the name already passed in (from the campaign) without erroring.
         if (finalBusinessName === 'Business Name') {
            try {
               const projectSnap = await getDoc(projectRef);
               if (projectSnap.exists()) {
                 finalBusinessName = projectSnap.data().name || 'Business Name';
               }
            } catch (e) {
               // project doc not publicly readable; keep existing name
            }
         }
      }

      // Helper to replace shortcodes
      const replaceShortcodes = (template: string) => {
        if (!template) return '';
        return template
          .replace(/{BusinessName}/g, finalBusinessName)
          .replace(/{GameName}/g, campaignName)
          .replace(/{PrizeName}/g, winningPrize?.name || (isRaffle ? 'Raffle Entry' : ''))
          .replace(/{TicketNumber}/g, generatedTicket || '')
          .replace(/{CampaignCode}/g, campaignCode || '');
      };

      // 4. Send Brevo Email
      try {
        if (email) {
          let subjectTemplate = globalConfig?.emailSubjectTemplate || '🎉 You won {PrizeName} from {BusinessName}!';
          if (isRaffle && !globalConfig?.emailSubjectTemplate) {
             subjectTemplate = '🎉 You are in the {GameName} raffle!';
          }
          let subject = replaceShortcodes(subjectTemplate);
          
          let contentTemplate = globalConfig?.emailContentTemplate || 'Congratulations! You have won {PrizeName} in our {GameName} campaign.\n\nYour ticket number is: {TicketNumber}\nCampaign Code: {CampaignCode}\n\nThanks for playing!';
          if (isRaffle && !globalConfig?.emailContentTemplate) {
             contentTemplate = 'You have successfully entered the {GameName} raffle.\n\nYour ticket number is: {TicketNumber}\nCampaign Code: {CampaignCode}\n\nThanks for playing!';
          }
          let customHtmlContent = replaceShortcodes(contentTemplate);

          await sendBrevoEmail({
            toEmail: email,
            toName: name,
            subject,
            prizeName: winningPrize?.name || '',
            campaignName: campaignName,
            isRaffle,
            ticketNumber: generatedTicket,
            fromName: globalConfig?.emailSenderName || finalBusinessName,
            campaignCode: campaignCode,
            customHtmlContent
          });
        }
      } catch (e) {
        console.error("Failed to send email:", e);
      }

      // 5. Send SignalWire SMS (If phone provided)
      try {
        if (phone) {
          let smsTemplate = globalConfig?.smsContentTemplate || 'Congrats! You won {PrizeName} from {BusinessName}. Ticket: {TicketNumber}. Code: {CampaignCode}';
          if (isRaffle && !globalConfig?.smsContentTemplate) {
             smsTemplate = 'You are in the {GameName} raffle! Ticket: {TicketNumber}. Code: {CampaignCode}';
          }
          let message = replaceShortcodes(smsTemplate);

          await sendSignalWireSms({
            toPhone: phone,
            message
          });
        }
      } catch (e) {
        console.error("Failed to send SMS:", e);
      }

      // 6. Sync Lead to External Database (myeSalon)
      if (isNewToProject) {
        try {
          console.log("[Sync] Syncing lead to external database...");
          const syncResponse = await fetch('/api/sync-lead', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              campaignId,
              leadData: { name, email, phone }
            })
          });
          
          const syncText = await syncResponse.text();
          let syncResult: any;
          try {
            syncResult = JSON.parse(syncText);
          } catch (e) {
            console.error("[Sync] Sync response is not valid JSON:", syncText.substring(0, 200));
            return;
          }

          if (!syncResponse.ok) {
            console.error("[Sync] External sync failed:", syncResult.error);
            if (syncResult.code === 'MISSING_WORKSPACE_USER') {
              console.warn("ATTENTION BUSINESS OWNER: Please add a Workspace User with API credentials in Project Settings to enable automatic lead synchronization with myeSalon.");
            }
          } else {
            console.log("[Sync] External sync successful:", syncResult);
          }
        } catch (err) {
          console.error("[Sync] Failed to sync lead to external database:", err);
        }
      } else {
        console.log("[Sync] Skipping myeSalon sync because user already exists in project database.");
      }

      if (isRaffle) {
        setTicketNumber(generatedTicket);
        setGameState('ticket_ready');
      } else {
        setGameState('idle');
        setEmail(''); setName(''); setPhone('');
        alert('Success! Reward details have been sent to you.');
      }
      
      if (campaignId !== 'local-test') {
        localStorage.setItem(`entered_${campaignId}_${type}`, 'true');
      }
    } catch (err: any) {
      console.error("Lead submission failed:", err);
      alert(err.message || "Something went wrong saving your entry. Please try again.");
      setGameState('lead_form');
    }
  };

  const renderGameEngine = () => {
    switch (type) {
      case GameType.RAFFLE: 
        return <Raffle prizes={prizes} endDate={endDate} onStart={() => handleStartGame()} isLightMode={effectiveLightMode} />;
      case GameType.SCRATCH_CARD: 
        return <ScratchCard gameState={gameState === 'playing' ? 'spinning' : gameState === 'revealed' ? 'finished' : 'idle'} winningPrize={winningPrize} onStart={() => handleStartGame()} onProgress={handleScratchProgress} isLightMode={effectiveLightMode} />;
      case GameType.SLOT_MACHINE: 
        return <SlotMachine prizes={prizes} slotValues={slotValues} gameState={gameState === 'playing' ? 'spinning' : 'idle'} onStart={() => handleStartGame()} theme={theme} isLightMode={effectiveLightMode} />;
      case GameType.MYSTERY_BOX:
        return <MysteryBox type={type} gameState={gameState === 'revealed' ? 'finished' : 'idle'} activeSelection={activeSelection} onStart={(i) => handleStartGame(i)} theme={theme} isLightMode={effectiveLightMode} />;
      case GameType.TRIVIA:
        return <Trivia questions={questions} onWin={handleTriviaWin} onLoss={handleTriviaLoss} isLightMode={effectiveLightMode} />;
      case GameType.GIFT_RAIN:
        return <GiftRain onWin={handleTriviaWin} isLightMode={effectiveLightMode} />;
      case GameType.SPIN_WHEEL: 
      default: 
        return <SpinWheel prizes={prizes} rotation={rotation} gameState={gameState === 'playing' ? 'spinning' : 'idle'} onStart={() => handleStartGame()} theme={theme} buttonText={buttonText} isLightMode={effectiveLightMode} />;
    }
  };

  return (
    <GameWrapper isLightMode={effectiveLightMode}>
      {gameState === 'submitting' && (
        <div className="flex flex-col items-center justify-center py-12 animate-pulse">
          <div className="w-16 h-16 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin mb-6"></div>
          <p className={`font-black uppercase tracking-widest text-xs ${effectiveLightMode ? 'text-slate-800' : 'text-white'}`}>Securing your reward...</p>
        </div>
      )}
      {gameState === 'lead_form' && (
        <LeadForm name={name} email={email} phone={phone} setName={setName} setEmail={setEmail} setPhone={setPhone} onSubmit={handleSubmitLead} isLightMode={effectiveLightMode} />
      )}
      {gameState === 'won' && (
        <SuccessView prize={winningPrize} onNext={() => setGameState('lead_form')} isLightMode={effectiveLightMode} />
      )}
      {gameState === 'ticket_ready' && (
        <TicketView ticketNumber={ticketNumber} endDate={endDate} onDone={() => setGameState('idle')} isLightMode={effectiveLightMode} />
      )}
      {gameState === 'already_entered' && (
        <div className={`flex flex-col items-center justify-center py-16 px-6 text-center rounded-[48px] border shadow-2xl ${effectiveLightMode ? 'bg-slate-50 border-slate-200' : 'bg-slate-900 border-slate-800'}`}>
          <div className="w-20 h-20 bg-indigo-500/20 rounded-full flex items-center justify-center mb-6">
            <svg className={`w-10 h-10 ${effectiveLightMode ? 'text-indigo-600' : 'text-indigo-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className={`font-black text-2xl mb-3 uppercase tracking-widest ${effectiveLightMode ? 'text-slate-800' : 'text-white'}`}>Already Entered!</h3>
          <p className="text-slate-400 text-sm font-medium max-w-xs">
            You have already secured your entry for this campaign. Stay tuned for the results!
          </p>
        </div>
      )}
      {gameState !== 'lead_form' && gameState !== 'won' && gameState !== 'ticket_ready' && gameState !== 'submitting' && gameState !== 'already_entered' && renderGameEngine()}
    </GameWrapper>
  );
};

export default GameSimulator;
